export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) { super(message); }
}

type Issue = { path?: unknown[]; message?: string };
function describe(data: { error?: string; details?: unknown }): string {
  const base = data.error ?? "Request failed";
  if (!Array.isArray(data.details) || !data.details.length) return data.error ?? `Request failed`;
  const parts = (data.details as Issue[]).filter((i) => i && typeof i.message === "string").map((i) => {
    const path = Array.isArray(i.path) ? i.path.join(".") : "";
    return path ? `${path}: ${i.message}` : String(i.message);
  });
  return parts.length ? `${base}: ${parts.join("; ")}` : base;
}

// The sign-in flow handles its own 401s (wrong password / wrong code) and must keep its CSRF token.
const SIGN_IN_PATHS = ["/api/auth/login", "/api/auth/totp"];

let csrf: string | null = null;
export const setCsrf = (t: string | null) => { csrf = t; };

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = {};
  // Behind Cloudflare/Traefik a body-less POST can reach the server as chunked with no content type, which Fastify
  // rejects (415). Every write therefore carries a JSON body, an empty object when there is nothing to send.
  const sendsBody = init.body !== undefined || method !== "GET";
  if (sendsBody) headers["content-type"] = "application/json";
  if (method !== "GET" && csrf) headers["x-csrf-token"] = csrf;
  const res = await fetch(path, {
    method, headers, credentials: "same-origin",
    body: sendsBody ? JSON.stringify(init.body ?? {}) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !SIGN_IN_PATHS.includes(path)) window.dispatchEvent(new Event("afhh:signed-out"));
    throw new ApiError(res.status, describe(data), data.details);
  }
  return data as T;
}
